import { Type } from 'class-transformer'
import dayjs from 'dayjs'
import { BaseEntity } from './base'
import type { PagingQuery } from './common'
import { DeadlineBadge, DeadlineType } from './deadline-type'
import { User } from './user'

export const deadlineDueTypes = ['Absolute', 'Relative'] as const
export type DeadlineDueType = (typeof deadlineDueTypes)[number]

export const intervalUoms = ['Minutes', 'Hours', 'Days', 'Weeks', 'Months', 'Years'] as const
export type IntervalUom = (typeof intervalUoms)[number]

export type DeadlineGroup = 'expired' | 'thisWeek' | 'thisMonth' | 'future'

export const deadlineStatuses = ['unfulfilled', 'fulfilled', 'canceled'] as const
export type DeadlineStatus = (typeof deadlineStatuses)[number]

export interface GetDeadlinesQuery extends PagingQuery {
  searchString?: string
  status?: DeadlineStatus
  userId?: string | null
  typeIds?: string[]
  badgeId?: string | null
  lastCursorDate?: string | null
  dueUpTo?: string
}

export class BaseDeadline extends BaseEntity {
  displayName!: string | null
  description!: string | null
  badgeId!: string | null
  @Type(() => DeadlineBadge)
  badge!: DeadlineBadge | null
  typeId!: string
  @Type(() => DeadlineType)
  type!: DeadlineType
  actualDate!: string | null
  fullfilledAt!: string | null
  fullfilledById!: string | null
  canceledAt!: string | null
  canceledById!: string | null
  duration!: number | null
  msEventId!: string | null
  reminderInterval!: number
  reminderIntervalUom!: IntervalUom
  dueType!: DeadlineDueType
  dueDate!: string | null
  dueInterval!: number | null
  dueIntervalUom!: IntervalUom | null
  dueIntervalFromId!: string | null
  dueIntervalTimeOverride!: number | null
  avoidSuspensionDays!: boolean | null
  practiceId!: string
  computedDueDate!: string
  reminderDueDate!: string

  public get timelineDate() {
    return this.actualDate || this.computedDueDate || this.dueDate || new Date().toISOString()
  }

  public get expired() {
    return dayjs(this.timelineDate).isBefore(dayjs())
  }

  public get label() {
    let label = this.type.displayName
    if (this.displayName) {
      label += ': ' + this.displayName
    }
    return label
  }

  public get tooltip() {
    return this.label + (this.badge ? ` (${this.badge.displayName})` : '')
  }

  public get icon() {
    return this.type?.icon
  }

  public get editable() {
    return this.fullfilledAt == null && this.canceledAt == null
  }

  public get deadlineGroup(): DeadlineGroup {
    if (this.expired) {
      return 'expired'
    }
    const expiresThisWeek = dayjs(this.timelineDate).subtract(1, 'week').isBefore(dayjs())
    if (expiresThisWeek) {
      return 'thisWeek'
    }
    const expiresThisMonth = dayjs(this.timelineDate).subtract(1, 'month').isBefore(dayjs())
    if (expiresThisMonth) {
      return 'thisMonth'
    }
    return 'future'
  }

  public get reminderMinutes() {
    if (this.reminderIntervalUom === 'Minutes') {
      return this.reminderInterval
    } else if (this.reminderIntervalUom === 'Hours') {
      return this.reminderInterval! * 60
    } else if (this.reminderIntervalUom === 'Days') {
      return this.reminderInterval! * 60 * 24
    } else if (this.reminderIntervalUom === 'Weeks') {
      return this.reminderInterval! * 60 * 24 * 7
    } else if (this.reminderIntervalUom === 'Months') {
      return this.reminderInterval! * 60 * 24 * 30
    } else if (this.reminderIntervalUom === 'Years') {
      return this.reminderInterval! * 60 * 24 * 365
    } else {
      return 0
    }
  }

  public get daysLeft() {
    return dayjs(this.timelineDate).diff(dayjs(), 'days')
  }
}

class UserDeadline extends BaseEntity {
  userId!: string
  deadlineId!: string

  @Type(() => User)
  user!: User
}

export class DeadlineRow extends BaseDeadline {
  @Type(() => UserDeadline)
  userDeadlines!: UserDeadline[]

  practice!: {
    id: string
    caseId: string
    displayName: string
    typeId: string
    phaseId: string
  }
}

export class Deadline extends DeadlineRow {
  @Type(() => BaseDeadline)
  dueIntervalFrom!: BaseDeadline | null
  @Type(() => User)
  fullfilledBy!: User | null
  @Type(() => User)
  canceledBy!: User | null
}

export interface GetUnfullfilledDeadlines {
  deadlines: DeadlineRow[]
}

export interface GetDeadlines {
  deadlines: DeadlineRow[]
  hasNextPage: boolean
  cursorBy: 'reminderDueDate' | 'computedDueDate'
}

export interface GetDeadline {
  deadline: Deadline
}

export class NewDeadline {
  typeId!: string
  practiceId!: string
  displayName?: string | null
  description?: string | null
  duration?: number | null
  userIds!: string[]
  dueType!: DeadlineDueType
  dueDate?: string | null
  dueInterval?: number
  dueIntervalUom?: IntervalUom
  dueIntervalFromId?: string
  dueIntervalTimeOverride?: number
  avoidSuspensionDays?: boolean
  createMsEvent?: boolean
  reminderInterval?: number
  reminderIntervalUom?: IntervalUom
}

export class SetDeadlineActualDate {
  actualDate!: string
}

export class EditDeadline {
  actualDate?: string | Date | null
  typeId?: string
  displayName?: string | null
  description?: string | null
  duration?: number | null
  badgeId?: string | null
  dueType?: DeadlineDueType
  dueDate?: string | Date | null
  dueInterval?: number | null
  dueIntervalUom?: IntervalUom | null
  dueIntervalFromId?: string | null
  dueIntervalTimeOverride?: number | null
  avoidSuspensionDays?: boolean | null
  createMsEvent?: boolean
  reminderInterval?: number | null
  reminderIntervalUom?: IntervalUom | null
}

export class FullfillDeadline {
  transitionPhase!: boolean
}

export interface DeadlineWritten {
  deadline: BaseDeadline
}
